import * as THREE from "three";
import { Cell } from "@highmarch/domain";

/**
 * The level camera's projections and ground geometry (isoCamera.ts): viewport ⇄ ground and world → viewport for a
 * camera and its viewport size, the screen's ground extent and axes, and the pan clamp at the map edge. Pure functions
 * of their arguments, so the camera maths stays unit-testable in Node.
 */

const GROUND = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

/** A camera and the CSS-pixel viewport it draws into. */
export interface CameraView { readonly camera: THREE.PerspectiveCamera; readonly width: number; readonly height: number }

/** Where a viewport point's ray meets the y=0 plane (null above the horizon). */
export function groundHit(v: CameraView, px: number, py: number): THREE.Vector3 | null {
  const ndc = new THREE.Vector2((px / v.width) * 2 - 1, -(py / v.height) * 2 + 1);
  const ray = new THREE.Raycaster();
  ray.setFromCamera(ndc, v.camera);
  const hit = new THREE.Vector3();
  return ray.ray.intersectPlane(GROUND, hit) ? hit : null;
}

/** Rounds of "intersect, re-read the height" in terrainHit. */
export const TerrainPickRounds = 3;

/**
 * Where a viewport point meets the ground when the ground is a height field, not the y=0 plane: intersect the
 * plane at the height found last time, re-read the height there, and repeat. Three rounds settle to well under a
 * cell on the rolling benches (their slopes are gentle next to the camera's pitch).
 */
export function terrainHit(v: CameraView, px: number, py: number, height: (x: number, z: number) => number): { x: number; z: number } | null {
  const ndc = new THREE.Vector2((px / v.width) * 2 - 1, -(py / v.height) * 2 + 1);
  const ray = new THREE.Raycaster();
  ray.setFromCamera(ndc, v.camera);
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const hit = new THREE.Vector3();
  for (let i = 0; i < TerrainPickRounds; i++) {
    if (!ray.ray.intersectPlane(plane, hit)) return null;
    plane.constant = -height(hit.x, hit.z);
  }
  return ray.ray.intersectPlane(plane, hit) ? { x: hit.x, z: hit.z } : null;
}

/** World point → CSS pixels within the viewport. */
export function toViewport(v: CameraView, x: number, y: number, z: number): { x: number; y: number } {
  const p = new THREE.Vector3(x, y, z).project(v.camera);
  return { x: ((p.x + 1) / 2) * v.width, y: ((1 - p.y) / 2) * v.height };
}

/** World position of a cell centre → normalised device coordinates. */
export function cellNdc(v: CameraView, c: Cell): THREE.Vector3 { return new THREE.Vector3(c.x, 0, c.y).project(v.camera); }

/** Cell → CSS pixels within the viewport (origin top-left). */
export function cellToPx(v: CameraView, c: Cell): { x: number; y: number } {
  const p = cellNdc(v, c);
  return { x: ((p.x + 1) / 2) * v.width, y: ((1 - p.y) / 2) * v.height };
}

/** Half the screen's ground extent at the look point (orthographic estimate), `distance` away at `tiltDeg` pitch. */
export function halfGround(camera: THREE.PerspectiveCamera, distance: number, tiltDeg: number): { right: number; forward: number } {
  const vFov = THREE.MathUtils.degToRad(camera.fov);
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
  return { right: distance * Math.tan(hFov / 2), forward: (distance * Math.tan(vFov / 2)) / Math.sin(THREE.MathUtils.degToRad(tiltDeg)) };
}

/** Screen-aligned ground axes at `yawDeg`: `right` runs along the screen's x, `forward` up the screen (away from the camera). */
export function screenAxes(yawDeg: number): { right: THREE.Vector3; forward: THREE.Vector3 } {
  const yaw = THREE.MathUtils.degToRad(yawDeg);
  return { right: new THREE.Vector3(-Math.cos(yaw), 0, Math.sin(yaw)), forward: new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)) };
}

/** The map as the pan clamp sees it: its centre and half extents in cells. */
export interface MapBox { readonly centre: THREE.Vector3; readonly halfW: number; readonly halfH: number }

/**
 * The look point clamped, per screen axis, so the uncovered band of the screen (`height` less the `top` and `bottom`
 * insets) never frames more than `margin` cells of shoreline beyond the map on that side; when the map is smaller than
 * the band along an axis it is centred in the band instead. Worked in screen-aligned ground coordinates so the rule
 * is the same at every yaw. Null when `look` is already inside.
 */
export function clampLook(look: THREE.Vector3, map: MapBox, axes: { right: THREE.Vector3; forward: THREE.Vector3 }, half: { right: number; forward: number },
                          height: number, top: number, bottom: number, margin: number): THREE.Vector3 | null {
  const { right, forward } = axes;
  // The band between the insets, as a forward half-extent and an offset of its centre from the screen centre.
  const bandHalfForward = half.forward * Math.max(0.2, (height - top - bottom) / height);
  const bandOffset = ((bottom - top) / height) * half.forward;
  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const corner = new THREE.Vector3(map.centre.x + sx * map.halfW, 0, map.centre.z + sz * map.halfH);
    const u = corner.dot(right), v = corner.dot(forward);
    minU = Math.min(minU, u); maxU = Math.max(maxU, u);
    minV = Math.min(minV, v); maxV = Math.max(maxV, v);
  }
  const range = (lo: number, hi: number, halfScreen: number): [number, number] => {
    const a = lo + halfScreen - margin, b = hi - halfScreen + margin;
    return a <= b ? [a, b] : [(lo + hi) / 2, (lo + hi) / 2];
  };
  const [uLo, uHi] = range(minU, maxU, half.right);
  const [vLo, vHi] = range(minV - bandOffset, maxV - bandOffset, bandHalfForward);
  const u = THREE.MathUtils.clamp(look.dot(right), uLo, uHi);
  const v = THREE.MathUtils.clamp(look.dot(forward), vLo, vHi);
  const clamped = right.clone().multiplyScalar(u).addScaledVector(forward, v);
  return clamped.distanceToSquared(look) < 1e-12 ? null : clamped;
}
